import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { UNITE_EVENT_PANE } from '@rsos/capstone/src/constants/capabilities';
import { UNITE_EVENT_PANE_PERMISSION } from '@rsos/capstone/src/constants/permissionNames';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { useFlag } from '@rsos/flags';
import { gspx } from '@rsos/gainsight';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasCapability from '@rsos/hooks/useHasCapability';
import useHasPermission from '@rsos/hooks/useHasPermission';
import {
  removeAllNonCallEvents,
  resetSelectedAirAmbulance,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import {
  removeAllCalls,
  viewAllCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  AVIVE_APP_LAUNCHER_MAP_CARD,
  MEDIA_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { hide as hideRade } from '../../JurisdictionView/RADECard/radeSlice';
import {
  Container,
  RemoveAll,
  Subtitle,
  Title,
  Wrapper,
} from './AllEventsLegacy.styles';
import RemoveAllEventsModalLegacy from './RemoveAllEventsModal/RemoveAllEventsModalLegacy';

/**
 *
 * @param {string} selectedCallerID - the selected caller id
 * @param {string} activeQueueTab - the selected tab (calls/alerts)
 * @param {boolean} hasCalls - if length of calls > 0
 *
 */
const AllEventsLegacy = ({
  hasCalls,
  activeQueueTab,
  selectedCallerID,
  onViewJurisdiction,
}) => {
  const hasUniteEventPaneCapabilities = useHasCapability(UNITE_EVENT_PANE);

  const hasUniteEventPanePermission = useHasPermission(
    UNITE_EVENT_PANE_PERMISSION,
  );

  const hasAirAmbulanceFlag = useFlag('air_ambulance');
  const hasAirAmbulanceAccess = useHasAccessToAirAmbulance();

  const selectedAirAmbulance = useSelector(
    state => state?.airAmbulance?.selectedAirAmbulance,
  );
  const airAmbulanceNonCalls = useSelector(
    state => state?.airAmbulance?.requests?.nonCalls,
  );

  const isUniteEventPaneEnabled = Boolean(
    hasUniteEventPaneCapabilities?.hasCapability &&
      hasUniteEventPanePermission?.hasPermission,
  );

  const hasNonCallAirAmbulances = useMemo(() => {
    return (
      Object.keys(airAmbulanceNonCalls).length &&
      hasAirAmbulanceAccess &&
      hasAirAmbulanceFlag &&
      isUniteEventPaneEnabled
    );
  }, [
    airAmbulanceNonCalls,
    hasAirAmbulanceAccess,
    isUniteEventPaneEnabled,
    hasAirAmbulanceFlag,
  ]);

  const dispatch = useDispatch();
  const { display_name: psapDisplayName } = useSelector(
    state => state.psaps.currentPSAP,
  );
  const { irpVersion } = useSelector(state => state.sinatra.irp);
  const { conversations: rcsConversations } = useSelector(state => state.rcs);
  const [removeAllVisible, setRemoveAllVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const animateProps = useSpring({
    config: { duration: 200 },
    display: removeAllVisible ? 'flex' : 'none',
    opacity: removeAllVisible ? 1 : 0,
  });

  const handleViewAll = () => {
    dispatch(viewAllCalls());
    dispatch(resetSelectedAirAmbulance());
    dispatch(hideRade());
    onViewJurisdiction();
    gspx.trackCustomEvent('Data Jurisdiction', {
      irpVersion,
      name: 'Click on All Calls',
      Category: 'Jurisdiction View',
      'Launched date': new Date(),
      serviceType: '',
      classOfServices: null,
    });
    dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    dispatch(closeCard(MEDIA_CARD));
  };

  const handleRightClick = e => {
    e.preventDefault();
    e.stopPropagation();

    gspx.trackCustomEvent('Data Jurisdiction', {
      irpVersion,
      name: 'Right-click on All Calls',
      Category: 'Jurisdiction View',
      'Launched date': new Date(),
    });

    if (
      activeQueueTab === EMERGENCY_CALLS &&
      !selectedCallerID &&
      !selectedAirAmbulance &&
      (hasCalls || hasNonCallAirAmbulances)
    ) {
      // remove all is not enabled for Alerts
      setRemoveAllVisible(true);
    }
  };

  const handleRemoveAllEvents = () => {
    gspx.trackCustomEvent('Data Jurisdiction', {
      irpVersion,
      name: 'Clicks on remove from remove all calls modal',
      Category: 'Jurisdiction View',
      'Launched date': new Date(),
    });
    if (activeQueueTab === EMERGENCY_CALLS) {
      dispatch(removeAllCalls());
      dispatch(removeAllNonCallEvents());
    } else {
      // next phase remove all alerts
      // dispatch(removeAllAlerts());
    }
    setRemoveAllVisible(false);
    setIsModalVisible(false);

    // dismiss all rcs toasts
    if (rcsConversations) {
      const callerIds = Object.keys(rcsConversations);
      callerIds.forEach(id => {
        toast.dismiss(`toastRcsAvailable-${id}`);
      });
    }
  };

  const titleText = 'View Jurisdiction';

  const onClickHandler = () => setRemoveAllVisible(false);

  window.oncontextmenu = () => {
    setIsModalVisible(false);
    window.removeEventListener('click', onClickHandler, false);
  };

  useEffect(() => {
    window.addEventListener('click', onClickHandler, false);

    return () => {
      window.removeEventListener('click', onClickHandler, false);
    };
  }, []);

  return (
    <>
      <Container onContextMenu={handleRightClick}>
        <Wrapper onClick={() => handleViewAll()} data-name="all-911-events">
          <Title>{titleText}</Title>
          {psapDisplayName && <Subtitle>{psapDisplayName}</Subtitle>}
        </Wrapper>
        <animated.div style={animateProps}>
          <RemoveAll
            data-name="remove-all"
            onClick={() => setIsModalVisible(true)}
          >
            Remove All
          </RemoveAll>
        </animated.div>
      </Container>
      <RemoveAllEventsModalLegacy
        activeQueueTab={activeQueueTab}
        isOpened={isModalVisible}
        onConfirm={handleRemoveAllEvents}
        onClose={() => setIsModalVisible(false)}
      />
    </>
  );
};

AllEventsLegacy.propTypes = {
  selectedCallerID: PropTypes.string,
};

export default AllEventsLegacy;
